/**
 * PHOTOS — the shop's roll, and the camera on a job.
 *
 * Two screens from one file because they are the same thing at different
 * scopes: a grid of images that opens one large.
 *
 * The camera is the point. `capture="environment"` on the file input is
 * what makes a phone open the back camera straight into the shutter
 * instead of a file browser — one tap from a job to a photo. On a desktop
 * the same input is an ordinary file picker, so nothing is lost.
 */

import {
  photosForJob, recentPhotos, addPhoto, DuplicatePhoto, removePhoto, setCaption, srcOf,
  KINDS, type Photo, type PhotoKind,
} from "../data/photos.ts";
import { captureNativePhoto, hasCamera, pickNativePhotos } from "../data/native.ts";
import { esc, ago, mount, since } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import type { Role } from "../domain/permissions.ts";

const label = (k: PhotoKind) => KINDS.find((x) => x.key === k)?.label ?? k;

async function tile(p: Photo): Promise<string> {
  const src = await srcOf(p);
  // "" means the link could not be signed. Say so, never a broken glyph.
  return `
    <button class="ph" data-id="${esc(p.id)}">
      ${src ? `<img loading="lazy" src="${esc(src)}" alt="${esc(p.caption ?? label(p.kind))}">`
        : `<span class="ph-c">This photo would not load. Refresh to try again.</span>`}
      <span class="ph-k">${esc(label(p.kind))}</span>
      ${p.caption ? `<span class="ph-c">${esc(p.caption)}</span>` : ""}
    </button>`;
}

async function grid(host: HTMLElement, photos: Photo[], onOpen: (p: Photo) => void): Promise<void> {
  if (!photos.length) {
    host.innerHTML = `<div class="empty">No photos yet.</div>`;
    return;
  }
  host.innerHTML = (await Promise.all(photos.map(tile))).join("");
  host.querySelectorAll<HTMLButtonElement>(".ph").forEach((b) =>
    b.addEventListener("click", () => {
      const p = photos.find((x) => x.id === b.dataset["id"]);
      if (p) onOpen(p);
    }));
}

/* ── one photo, large ─────────────────────────────────────────────── */

async function open(p: Photo, role: Role, after: () => void): Promise<void> {
  document.getElementById("lightbox")?.remove();
  const src = await srcOf(p);
  const mayEdit = role !== "viewer";

  document.body.insertAdjacentHTML("beforeend", `
    <div class="lightbox" id="lightbox">
      <button class="lb-x" id="lb-x" aria-label="Close">&times;</button>
      ${src ? `<img src="${esc(src)}" alt="${esc(p.caption ?? label(p.kind))}">`
        : `<p class="quiet">This photo would not load. Close it and try again.</p>`}
      <div class="lb-bar">
        <span class="pill">${esc(label(p.kind))}</span>
        <span class="quiet">${esc(since(p.taken_at))}</span>
        ${mayEdit ? `
          <input id="lb-cap" aria-label="Caption"
                 value="${esc(p.caption ?? "")}" maxlength="140">
          <button class="btn sm" id="lb-save">Save</button>
          <button class="btn ghost sm" id="lb-del">Delete</button>` : ""}
      </div>
    </div>`);

  /**
   * GETTING OUT. Before this, a photo opened full screen and the only way
   * back was a small × or a sliver of backdrop — on a phone, where the
   * photo fills the screen and there is no Escape key, that was no way at
   * all. Anthony: "you click a photo and it just blows up and you can't get
   * out." So now a tap anywhere closes it — the backdrop AND the photo
   * itself — and Escape closes it on a desktop. The one exception is the
   * caption bar, so editing a caption does not dismiss the thing you are
   * captioning.
   */
  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") shut(); };
  const shut = (): void => {
    document.getElementById("lightbox")?.remove();
    document.removeEventListener("keydown", onKey);
  };
  document.addEventListener("keydown", onKey);
  document.getElementById("lb-x")?.addEventListener("click", shut);
  document.getElementById("lightbox")?.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest(".lb-bar")) shut();
  });

  document.getElementById("lb-save")?.addEventListener("click", async () => {
    const v = (document.getElementById("lb-cap") as HTMLInputElement).value;
    try { await setCaption(p.id, v); shut(); after(); } catch { /* leave it open */ }
  });

  document.getElementById("lb-del")?.addEventListener("click", async () => {
    const b = document.getElementById("lb-del") as HTMLButtonElement;
    // Two taps, because a photo is the thing you cannot take again.
    if (b.dataset["sure"] !== "1") {
      b.dataset["sure"] = "1"; b.textContent = "Really delete?";
      return;
    }
    try { await removePhoto(p); shut(); after(); } catch { /* leave it open */ }
  });
}

/* ── the camera, on a job ─────────────────────────────────────────── */

/**
 * Mounted into a host element on the job card. Not a page of its own,
 * because taking a photo is something you do while looking at the job.
 */
export function jobPhotos(
  hostId: string, jobId: string, vehicleId: string | null, role: Role,
  /** Where it is mounted decides what a new photo is filed as. */
  startAs: PhotoKind = "damage",
  /**
   * FOLDED UNTIL THERE IS WORK TO PHOTOGRAPH.
   *
   * Anthony: "show photo tile on lead/quote/job everywhere but folded in
   * until job starts near bottom of card."
   *
   * A quote nobody has started has no photos, and a camera plus four
   * empty category chips plus a caption box is a third of a phone screen
   * spent saying "nothing here yet". Shut, it is one line. It opens
   * itself the moment the job starts, which is the moment somebody is
   * standing at the car with a phone in their hand.
   *
   * A <details> rather than a button, so it keeps its own state, works
   * with no JavaScript, and a photo count on the summary means nobody has
   * to open it to find out whether it is worth opening.
   */
  fold?: { shut: boolean },
): void {
  const host = document.getElementById(hostId);
  if (!host) return;
  const mayAdd = role !== "viewer";
  let kind: PhotoKind = startAs;

  const draw = async (): Promise<void> => {
    const head = `Photos <i class="phn" id="phn-${esc(hostId)}"></i>`;
    /**
     * ONE TAP FROM SHUT.
     *
     * Folding the card won back a third of the screen and cost the thing
     * the card is for. Shut, the whole row reads "Photos" and a count:
     * the camera, the gallery and the categories are all behind it, and
     * the person holding the phone is standing at a car with one hand
     * free. 827d067 moved the fold so it opens once a job starts, which
     * helped nobody entering a walk-in — newjob.ts writes those as
     * `estimate`, and an estimate is exactly the state that stays shut.
     *
     * So the shutter comes out onto the summary, where it is reachable
     * without opening anything. It disappears once the card is open,
     * because the full one is right there.
     */
    const shot = mayAdd && fold
      ? `<button type="button" class="ph-shot" id="ph-shot"
           title="Take a photo" aria-label="Take a photo">${icon("camera")}</button>`
      : "";
    host.innerHTML = `
      ${fold
        ? `<details class="phfold"${fold.shut ? "" : " open"}>
             <summary class="card-head">${head}${shot}</summary>`
        : `<div class="card-head">${head}</div>`}
      <div class="pad">
        ${mayAdd ? `
          <div class="ph-kinds" id="ph-kinds">
            ${KINDS.map((k) => `<button class="chip ${k.key === kind ? "on" : ""}"
              data-k="${k.key}" title="${esc(k.what)}">${esc(k.label)}</button>`).join("")}
          </div>
          <!-- WHAT IT IS A PHOTO OF, said while the technician is still
               standing at the car. Anthony: "make certain technicians can
               notate what the photo is of when they upload it, category
               and a short description, both optional."

               Optional means optional: the box is empty, nothing stops an
               upload, and the button says "Take a photo" not "Describe
               and take a photo". Somebody under a car with one hand free
               must never be blocked on typing. But a caption typed there
               and then is worth ten written from memory a week later, so
               it is offered at the moment it is easy.

               It applies to the whole batch, because eight photos of one
               job are eight photos of the same thing and typing it eight
               times is how nobody types it once. -->
          <label class="quiet small" for="ph-note">What is it — goes on all of them</label>
          <input class="ph-note" id="ph-note" maxlength="120" autocomplete="off">
          <!-- TWO DOORS, because capture closes one of them.

               The capture attribute does not mean "prefer the camera".
               On a phone it means the gallery is never offered at all --
               the picker goes straight to the shutter and there is no way
               back. Alaina had photos already taken on her phone and no
               way to put them on a job.

               So the camera keeps its own input and its own button, and
               the plain one sits beside it. On a desktop both are an
               ordinary file picker, which is why this was invisible to
               everyone testing on a laptop. -->
          <div class="ph-add">
            <input type="file" id="ph-file" accept="image/*" capture="environment" multiple hidden>
            <input type="file" id="ph-pick" accept="image/*" multiple hidden>
            <button type="button" class="btn primary" id="ph-btn">Take a photo</button>
            <button type="button" class="btn" id="ph-pick-btn">From the gallery</button>
          </div>
          <div class="ph-status" id="ph-status"></div>` : ""}
        <div class="phgrid" id="phgrid"><div class="loading">Reading&hellip;</div></div>
      </div>
      ${fold ? "</details>" : ""}`;

    document.querySelectorAll<HTMLButtonElement>("#ph-kinds .chip").forEach((b) =>
      b.addEventListener("click", () => {
        kind = b.dataset["k"] as PhotoKind;
        document.querySelectorAll("#ph-kinds .chip").forEach((x) => x.classList.remove("on"));
        b.classList.add("on");
      }));

    const file = document.getElementById("ph-file") as HTMLInputElement | null;
    const pick = document.getElementById("ph-pick") as HTMLInputElement | null;
    document.getElementById("ph-btn")?.addEventListener("click", () => {
      if (native) { void nativeShoot(); return; }
      file?.click();
    });
    // preventDefault, or the tap that opens the camera also toggles the
    // <details> it sits on — and the card would shut again behind the
    // picker. It is opened deliberately instead, so the photo lands
    // somewhere the person can see it arrive.
    document.getElementById("ph-shot")?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      host.querySelector<HTMLDetailsElement>("details.phfold")?.setAttribute("open", "");
      if (native) { void nativeShoot(); return; }
      file?.click();
    });
    document.getElementById("ph-pick-btn")?.addEventListener("click", () => {
      if (native) { void nativePick(); return; }
      pick?.click();
    });

    /* Split from absorb() on 2026-09-20 so the native camera and the file
       input share one upload path. Everything below this line -- the
       one-at-a-time loop, the caption, the counter -- was already right and
       is not worth having twice. */
    const send = async (files: File[], clearInput?: HTMLInputElement): Promise<void> => {
      if (!files.length) return;
      const status = document.getElementById("ph-status");
      const noteEl = document.getElementById("ph-note") as HTMLInputElement | null;
      const note = (noteEl?.value ?? "").trim().slice(0, 120);
      let done = 0;
      let skipped = 0;

      // One at a time. Eight parallel uploads on a car park signal is how
      // you get eight timeouts instead of eight photos.
      for (const f of files) {
        if (status) {
          status.innerHTML = `<span class="msg">Sending ${done + 1} of ${files.length}&hellip;</span>`;
        }
        try {
          try {
            await addPhoto(jobId, vehicleId, f, kind, note);
          } catch (e) {
            if (!(e instanceof DuplicatePhoto)) throw e;
            /* Last week's photos sit first in a phone's gallery, which is how
               one Tahoe ended up on four jobs. Asked, not refused: the same
               receipt can belong on two jobs, and the person knows. */
            if (!window.confirm(`${e.message} Add it to this job too?`)) {
              skipped++;
              continue;
            }
            await addPhoto(jobId, vehicleId, f, kind, note, { allowDuplicate: true });
          }
          done++;
        } catch (e) {
          if (status) {
            status.innerHTML = `<span class="msg bad">${
              esc(e instanceof Error ? e.message : String(e))}</span>`;
          }
          break;
        }
      }
      if (status && done + skipped === files.length) {
        status.innerHTML = `<span class="msg ok">${done} added${
          note ? ` as ${esc(note)}` : ""}${skipped ? `, ${skipped} left off` : ""}.</span>`;
        setTimeout(() => { status.innerHTML = ""; }, 2500);
      }
      // Cleared, because the next photo is usually of something else and a
      // caption left in the box quietly mislabels it.
      if (noteEl) noteEl.value = "";
      if (clearInput) clearInput.value = "";
      await fill();
    };

    const absorb = (input: HTMLInputElement): Promise<void> =>
      send(Array.from(input.files ?? []), input);

    /* THE APP TAKES ITS OWN PHOTOS.
       Inside the Capacitor shell the two buttons call the Camera plugin
       instead of clicking a hidden <input>. Three things this buys that the
       input cannot, all of which had already cost somebody something:

       - Orientation. getPhoto rotates the pixels before handing them over.
         shrink() re-encodes through a canvas and a canvas ignores EXIF, so
         a sideways photo used to land in the job sideways.
       - HEIC. An iPhone saves HEIC, no browser decodes it, and addPhoto has
         to refuse it with a note about changing a setting in Settings >
         Camera. getPhoto hands back JPEG whatever the phone saves, so on
         the app that message can no longer be reached.
       - Apple 4.2.2. A shell that only wraps a website is rejected. This is
         the app doing something the website cannot.

       It falls back to the input whenever the plugin is missing, which is
       every web browser and any shell older than the plugin. */
    const native = hasCamera();

    const nativeShoot = async (): Promise<void> => {
      const status = document.getElementById("ph-status");
      try {
        const shot = await captureNativePhoto();
        // null is the person tapping Cancel, which is not an error and
        // must not leave a message on screen.
        if (shot) await send([shot]);
      } catch (e) {
        if (status) {
          status.innerHTML = `<span class="msg bad">${
            esc(e instanceof Error ? e.message : String(e))}</span>`;
        }
      }
    };

    const nativePick = async (): Promise<void> => {
      const status = document.getElementById("ph-status");
      try {
        await send(await pickNativePhotos());
      } catch (e) {
        if (status) {
          status.innerHTML = `<span class="msg bad">${
            esc(e instanceof Error ? e.message : String(e))}</span>`;
        }
      }
    };

    file?.addEventListener("change", () => void absorb(file));
    pick?.addEventListener("change", () => void absorb(pick));

    await fill();
  };

  const fill = async (): Promise<void> => {
    const g = document.getElementById("phgrid");
    if (!g) return;
    try {
      const shots = await photosForJob(jobId);
      // The count goes on the summary so a shut fold still says whether
      // there is anything behind it.
      const n = document.getElementById(`phn-${hostId}`);
      if (n) n.textContent = shots.length ? String(shots.length) : "";
      await grid(g, shots, (p) => void open(p, role, () => void fill()));
    } catch (e) {
      g.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
    }
  };

  void draw();
}

/* ── the whole shop's roll ────────────────────────────────────────── */

let filter: PhotoKind | undefined;

export async function photosPage(role: Role = "viewer"): Promise<void> {
  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Photos</h1>
      <p>Every shot on every job, newest first. Open one to caption it.</p></header>
    <div class="tabs wide tabs-wrap" id="ph-filter">
      <button class="tab ${filter ? "" : "on"}" data-k="">Everything</button>
      ${KINDS.map((k) => `<button class="tab ${filter === k.key ? "on" : ""}"
        data-k="${k.key}">${esc(k.label)}</button>`).join("")}
    </div>
    <div id="findhost">${findBarHtml("Find a photo by its caption")}</div>
    <div class="phgrid big" id="roll"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  document.querySelectorAll<HTMLButtonElement>("#ph-filter .tab").forEach((b) =>
    b.addEventListener("click", () => {
      filter = (b.dataset["k"] || undefined) as PhotoKind | undefined;
      void photosPage(role);
    }));

  const host = document.getElementById("roll");
  if (!host) return;
  try {
    await grid(host, await recentPhotos(filter), (p) => void open(p, role, () => void photosPage(role)));
    wireFindBar({ hostId: "findhost", listId: "roll", noun: "photo" });
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}
